import * as React from 'react';
import Autosuggest, {
  InputProps,
  RenderInputComponentProps,
} from 'react-autosuggest';
import { useState, forwardRef } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import SearchServiceInput from '@app/components/SearchServiceInput/SearchServiceInput';
import SearchServiceSuggestion from '@app/components/SearchServiceSuggestion/SearchServiceSuggestion';

import { AppState } from '@app/store';
import { loadSuggestions } from '@app/store/search/thunks';
import { clearSuggestions, setSearchValue } from '@app/store/search/actions';
import { Suggestion } from '@models/Suggestion/Suggestion.interface';

import { theme } from './autoSuggestionTheme';
import './style.scss';
import { SearchServiceProps } from './SearchServiceProps';
import { useSpatz } from '@app/hooks/useSpatz';

const SearchService: React.FC<SearchServiceProps> = ({
  loadSuggestions,
  suggestions,
  debounceTime,
  clearSuggestions,
  initialValue,
  setSearchValue,
  forwardedRef,
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [inputFocused, setInputFocused] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<number | undefined>(
    undefined
  );
  const [blurTimeout, setBlurTimeout] = useState<number | undefined>(undefined);
  const { sendSpatzStats } = useSpatz({ Services: { catalog: {} } });

  const handleFocus = () => {
    if (!inputFocused)
      sendSpatzStats({ Поиск: { 'активация поля': 'onfocus' } });
    setInputFocused(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    clearSuggestions();
    // Для того, чтобы при нажатии на кнопки
    // у инпута не слетал фокус
    e.persist();
    clearTimeout(blurTimeout);
    setBlurTimeout(
      window.setTimeout(() => {
        setInputFocused(false);
        e.target.blur();
      }, 200)
    );
  };

  const handleClear = () => {
    setInputValue('');
    setSearchValue('');
    clearTimeout(blurTimeout);
    document.getElementById('mos-catalog-search-input')!.focus();
  };

  const handleChange = (
    _event: React.FormEvent<HTMLElement>,
    { newValue }: Autosuggest.ChangeEvent
  ) => {
    setInputValue(newValue);
    setSearchValue(newValue);
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    event.preventDefault();
    if (event.keyCode === 13) {
      sendSpatzStats({
        globalSearch: {
          eventType: 'enter',
          searchType: 'Поле поиска',
        },
      });
      goToSearchPage(inputValue);
    }
  };

  const getSuggestionValue = (suggestion: Suggestion) => suggestion.title;

  const goToSearchPage = (query: string) => {
    window.open(
      `https://www.mos.ru/search?category=services&page=1&q=${encodeURIComponent(
        query
      )}&referer=${encodeURIComponent(window.location.href)}`
    );
  };

  const onSuggestionSelected = (
    event: React.FormEvent,
    { suggestionIndex: index }: { suggestionIndex: number }
  ) => {
    const selectedSuggestion = suggestions[index];
    const eventType = event.type === 'keydown' ? 'enter' : 'click';

    if (selectedSuggestion && selectedSuggestion.url) {
      sendSpatzStats({
        fastSearch: {
          eventType,
          text: selectedSuggestion.title,
        },
      });
      window.open(selectedSuggestion.url);
    } else if (index === suggestions.length - 1) {
      sendSpatzStats({
        globalSearch: {
          eventType,
          searchType: 'Расширенный поиск',
        },
      });
      goToSearchPage(inputValue);
    }
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(undefined);
    }
    setDebounceTimer(
      window.setTimeout(() => {
        loadSuggestions({ value });
      }, debounceTime || 500)
    );
  };

  const inputProps: InputProps<Suggestion> = {
    value: inputValue,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyUp: handleKeyUp,
  };

  const renderInputComponent = (inputProps: RenderInputComponentProps) => (
    <SearchServiceInput
      inputProps={inputProps}
      isFilled={!!(inputValue.length && inputValue.length > 0)}
      isFocused={inputFocused}
      searchHandler={() => {
        sendSpatzStats({
          globalSearch: {
            eventType: 'click',
            searchType: 'Поле поиска',
          },
        });
        goToSearchPage(inputValue);
      }}
      clearHandler={handleClear}
    />
  );

  const renderSuggestionComponents = (
    suggestion: Suggestion,
    { query }: { query: string }
  ) => {
    return (
      <SearchServiceSuggestion
        query={query}
        suggestion={suggestion}
        theme={theme}
      />
    );
  };

  return (
    <div
      ref={forwardedRef}
      className={cn('mos-catalog-search__sticky', {
        'mos-catalog-search__sticky--focused': inputFocused,
      })}
    >
      <div
        className={cn('mos-catalog-search', {
          'mos-catalog-search--focused': inputFocused,
        })}
      >
        <Autosuggest
          theme={theme}
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={clearSuggestions}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestionComponents}
          renderInputComponent={renderInputComponent}
          onSuggestionSelected={onSuggestionSelected}
          inputProps={inputProps}
          containerProps={{ 'aria-label': 'Найти услугу' }}
        />
      </div>
    </div>
  );
};

const SearchServiceWithRef = forwardRef<HTMLDivElement, SearchServiceProps>(
  (props, ref) => {
    return <SearchService forwardedRef={ref} {...props} />;
  }
);

const mapStateToProps = (state: AppState) => ({
  suggestions: state.search.suggestions,
  initialValue: state.search.searchValue,
});

const dispatchProps = {
  loadSuggestions,
  clearSuggestions,
  setSearchValue,
};

const SearchServiceConnected = connect(mapStateToProps, dispatchProps, null, {
  forwardRef: true,
})(SearchServiceWithRef);

export default SearchServiceConnected;
